import { getRegionCodes } from '@luxuryescapes/lib-regions';
import type { Brand } from '@luxuryescapes/lib-regions/lib/regions';

import { LUX_PLUS } from '~/consts/membership';

import { isBeforeOrSame } from '~/services/TimeService';

import { isSameCampaignScheduleDate } from './campaignScheduleDates';
import { BRAND_MAP, MARKET_REGION_MAP } from './deriveCampaignSchedulePreview';
import type {
  BrandSchedule,
  CampaignScheduleImportCandidate,
  CampaignScheduleParentRow,
  CampaignScheduleParentType,
  ConflictingSchedule,
} from './types';

// Mirrors svc-offer's schedule dependency chain (scheduleValidator.js). Exported so useImportCampaignSchedules
// can derive its submit ordering from the same source instead of maintaining a second priority map.
export const REQUIRED_TYPES: Record<CampaignScheduleParentType, Array<CampaignScheduleParentType>> = {
  availability: [],
  online_purchase: ['availability'],
  list_visibility: ['availability', 'online_purchase'],
  lux_plus: ['availability', 'online_purchase'],
};

function isContained(
  child: Pick<CampaignScheduleImportCandidate, 'start' | 'end'>,
  parent: { start: string; end: string },
): boolean {
  return isBeforeOrSame(parent.start, child.start) && isBeforeOrSame(child.end, parent.end);
}

function isCandidateMatched(
  candidate: Pick<CampaignScheduleImportCandidate, 'type' | 'brand' | 'region' | 'start' | 'end'>,
  brandSchedules: Array<BrandSchedule>,
): boolean {
  return brandSchedules.some(
    (schedule) =>
      schedule.type === candidate.type &&
      schedule.brand === candidate.brand &&
      schedule.region === candidate.region &&
      isSameCampaignScheduleDate(schedule.start, candidate.start) &&
      isSameCampaignScheduleDate(schedule.end, candidate.end),
  );
}

// public.schedule has a unique constraint on (offer, type, region, brand) with no date component, so a
// schedule with different dates for the same triple must be updated in place, not inserted.
function findConflictingSchedule(
  candidate: Pick<CampaignScheduleImportCandidate, 'type' | 'brand' | 'region'>,
  brandSchedules: Array<BrandSchedule>,
): ConflictingSchedule | undefined {
  const schedule = brandSchedules.find(
    (schedule) =>
      schedule.type === candidate.type && schedule.brand === candidate.brand && schedule.region === candidate.region,
  );

  return (
    schedule && {
      id: schedule.id,
      start: schedule.start,
      end: schedule.end,
      marketingChannels: schedule.marketing_channels,
    }
  );
}

// Prefers a same-batch candidate (the post-import truth, even when replacing an existing row) over a
// persisted schedule; falls back to the persisted schedule (exact region, or "world") otherwise.
function findDependencyWindow(
  type: CampaignScheduleParentType,
  brand: string,
  region: string,
  candidates: Array<CampaignScheduleImportCandidate>,
  brandSchedules: Array<BrandSchedule>,
): { start: string; end: string } | null {
  const candidate = candidates.find(
    (c) => c.type === type && c.brand === brand && (c.region === region || c.region === 'world'),
  );

  if (candidate) {
    return { start: candidate.start, end: candidate.end };
  }

  const existing = brandSchedules.find(
    (schedule) =>
      schedule.type === type && schedule.brand === brand && (schedule.region === region || schedule.region === 'world'),
  );

  return existing ? { start: existing.start, end: existing.end } : null;
}

function getDependencyError(
  candidate: CampaignScheduleImportCandidate,
  candidates: Array<CampaignScheduleImportCandidate>,
  brandSchedules: Array<BrandSchedule>,
): string | undefined {
  for (const requiredType of REQUIRED_TYPES[candidate.type]) {
    const window = findDependencyWindow(requiredType, candidate.brand, candidate.region, candidates, brandSchedules);

    if (!window) {
      // Omits brand/region deliberately — groupCandidatesByRegion groups by this exact string, and
      // including brand/region would give every region a unique string and defeat that grouping.
      return `Requires an existing or selected ${requiredType} schedule`;
    }

    if (!isContained(candidate, window)) {
      return `${candidate.type} dates must fall within the ${requiredType} schedule's dates`;
    }
  }

  return undefined;
}

// public.schedule requires one brand + one region per row, so each parent row expands into one candidate per
// brand x region combination.
export function buildImportCandidates(
  parentRows: Array<CampaignScheduleParentRow>,
  brandSchedules: Array<BrandSchedule>,
): { candidates: Array<CampaignScheduleImportCandidate>; warnings: Array<string> } {
  const candidates: Array<CampaignScheduleImportCandidate> = [];
  const unmappedBrands = new Set<string>();
  const unmappedMarkets = new Set<string>();
  const ineligibleLuxPlusMarkets = new Set<string>();
  // brandLabel -> set of "marketLabel (region)" invalid for that brand; grouped into warnings below.
  const invalidMarketsByBrand = new Map<string, Set<string>>();

  function pushCandidate(
    parentRow: CampaignScheduleParentRow,
    fields: { brand: string; region: string; brandLabel: string; marketLabel: string },
  ) {
    const { brand, region, brandLabel, marketLabel } = fields;

    candidates.push({
      key: `${parentRow.id}-${brand}-${region}`,
      parentRowId: parentRow.id,
      type: parentRow.type,
      brand,
      region,
      brandLabel,
      marketLabel,
      start: parentRow.start,
      end: parentRow.end,
      isMatched: isCandidateMatched(
        { type: parentRow.type, brand, region, start: parentRow.start, end: parentRow.end },
        brandSchedules,
      ),
    });
  }

  parentRows.forEach((parentRow) => {
    parentRow.brands.forEach((brandLabel) => {
      const brand = BRAND_MAP[brandLabel];

      if (!brand) {
        unmappedBrands.add(brandLabel);
        return;
      }

      // Mirrors svc-offer's createList handler, which silently drops brand/region combinations
      // invalid for that brand instead of erroring — catch it here instead of after a failed import.
      const validRegionCodes = new Set(getRegionCodes(brand as Brand));

      parentRow.markets.forEach((marketLabel) => {
        const region = MARKET_REGION_MAP[marketLabel];

        if (!region) {
          unmappedMarkets.add(marketLabel);
          return;
        }

        // Mirrors the LUX_PLUS.REGIONS allowlist ScheduleBulkUpdate.tsx uses — an admin-portal
        // convention, not something svc-offer's schema itself enforces.
        if (parentRow.type === 'lux_plus' && !LUX_PLUS.REGIONS.includes(region as App.MembershipRegion)) {
          ineligibleLuxPlusMarkets.add(marketLabel);
          return;
        }

        if (!validRegionCodes.has(region)) {
          const invalidMarkets = invalidMarketsByBrand.get(brandLabel) ?? new Set<string>();
          invalidMarkets.add(`${marketLabel} (${region})`);
          invalidMarketsByBrand.set(brandLabel, invalidMarkets);
          return;
        }

        pushCandidate(parentRow, { brand, region, brandLabel, marketLabel });
      });
    });
  });

  const candidatesWithDependencies = candidates.map((candidate) => {
    if (candidate.isMatched) {
      return candidate;
    }

    return {
      ...candidate,
      conflictingSchedule: findConflictingSchedule(candidate, brandSchedules),
      dependencyError: getDependencyError(candidate, candidates, brandSchedules),
    };
  });

  const warnings: Array<string> = [];

  unmappedBrands.forEach((brand) =>
    warnings.push(`Brand__c value "${brand}" has no known svc-offer brand mapping — cannot be imported`),
  );
  unmappedMarkets.forEach((market) =>
    warnings.push(`Market__c value "${market}" has no known svc-offer region mapping — cannot be imported`),
  );
  ineligibleLuxPlusMarkets.forEach((market) =>
    warnings.push(`LuxPlus+ is not available in market "${market}" — schedule will not be imported`),
  );
  buildInvalidRegionWarnings(invalidMarketsByBrand).forEach((warning) => warnings.push(warning));

  return { candidates: candidatesWithDependencies, warnings };
}

// Groups brands invalid for the exact same market set into one warning line instead of one per
// brand x market combination.
function buildInvalidRegionWarnings(invalidMarketsByBrand: Map<string, Set<string>>): Array<string> {
  const brandsBySignature = new Map<string, { markets: Array<string>; brands: Array<string> }>();

  invalidMarketsByBrand.forEach((marketsSet, brandLabel) => {
    const markets = Array.from(marketsSet).sort();
    const signature = markets.join('|');
    const existing = brandsBySignature.get(signature);

    if (existing) {
      existing.brands.push(brandLabel);
    } else {
      brandsBySignature.set(signature, { markets, brands: [brandLabel] });
    }
  });

  return Array.from(brandsBySignature.values()).map(({ markets, brands }) => {
    const brandList = brands.join(', ');
    const marketList = markets.join(', ');
    const isSingle = brands.length === 1 && markets.length === 1;

    return `${brandList} ${brands.length === 1 ? 'is' : 'are'} not sold in ${marketList} — ${isSingle ? 'this schedule' : 'these schedules'} will not be imported`;
  });
}

// Surfaces the modal's per-row dependencyError as tab-level warnings, grouped by (type, reason) since
// the same blocker often affects every brand/region combination at once.
export function buildDependencyWarnings(candidates: Array<CampaignScheduleImportCandidate>): Array<string> {
  const groups = new Map<string, { type: string; dependencyError: string; count: number }>();

  candidates.forEach((candidate) => {
    if (!candidate.dependencyError) {
      return;
    }

    const key = `${candidate.type}::${candidate.dependencyError}`;
    const existing = groups.get(key);

    if (existing) {
      existing.count += 1;
    } else {
      groups.set(key, { type: candidate.type, dependencyError: candidate.dependencyError, count: 1 });
    }
  });

  return Array.from(groups.values()).map(
    ({ type, dependencyError, count }) =>
      `${type}: ${dependencyError} (${count} schedule row${count === 1 ? '' : 's'} affected)`,
  );
}
